import React, { useState } from 'react';
import { IconPickerButton, IconPickerCard } from './common/IconPicker';

interface LeagueInfoFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
  icon: string;
  onIconChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  isPublic: boolean;
  onIsPublicChange: (value: boolean) => void;
  publicDisabled?: boolean;
  publicNote?: string;
  publicWarning?: React.ReactNode;
}

const LeagueInfoFields: React.FC<LeagueInfoFieldsProps> = ({
  name,
  onNameChange,
  icon,
  onIconChange,
  description,
  onDescriptionChange,
  isPublic,
  onIsPublicChange,
  publicDisabled,
  publicNote = 'Public leagues can be joined by anyone when creating a player. Private leagues can only be joined through league management.',
  publicWarning,
}) => {
  const [iconExpanded, setIconExpanded] = useState(false);

  return (
    <>
      <div>
        <label htmlFor="leagueName" className="block text-sm font-medium text-gray-700 mb-2">
          League Name *
        </label>
        <div className="flex items-start gap-3">
          <input
            type="text"
            id="leagueName"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter league name"
            maxLength={50}
            required
          />
          <div className="shrink-0">
            <IconPickerButton value={icon} onClick={() => setIconExpanded(!iconExpanded)} />
          </div>
        </div>
      </div>

      {iconExpanded && (
        <div className="mt-3">
          <IconPickerCard
            value={icon}
            onChange={onIconChange}
            onClose={() => setIconExpanded(false)}
          />
        </div>
      )}

      <div className="mt-6">
        <label htmlFor="leagueDescription" className="block text-sm font-medium text-gray-700 mb-2">
          Description (Optional)
        </label>
        <textarea
          id="leagueDescription"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Describe your league..."
          maxLength={200}
        />
      </div>

      <div className="mt-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isPublic"
            checked={isPublic}
            onChange={(e) => onIsPublicChange(e.target.checked)}
            disabled={publicDisabled}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <label htmlFor="isPublic" className="ml-2 block text-sm font-medium text-gray-700">
            Public League
          </label>
        </div>
        <p className="mt-1 text-sm text-gray-500">{publicNote}</p>
        {publicWarning}
      </div>
    </>
  );
};

export default LeagueInfoFields;
